import { FC } from 'react';
import ReactJson from '@microlink/react-json-view';

import Typography from '@material-ui/core/Typography';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import IProduct from 'models/product';
import { Accordion, AccordionSummary } from './ImageTranscriber';
import { resize128ImageWithCompression } from 'utils/images-ts';

const useStyles = makeStyles(theme => ({
  heading: {
    flexBasis: '50%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  entryAccordion: {
    margin: '8px 0 !important',
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    '&:before': {
      display: 'none',
    },
  },
  timestamp: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  imageList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
  },
  thumbnail: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
  },
  fileThumbail: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 60,
    width: 60,
    color: 'rgba(0, 0, 0, 0.87)',
    opacity: 0.3,
    background: 'rgba(0, 0, 0, 0.15)',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 20,
    fontWeight: 600,
  }
}));

export interface IMagicUploadEntry {
  timestamp: string;
  imageUrls: string[];
  rawOutput: Record<string, any>;
  model: string;
}

interface MagicUploadPreviewProps {
  state?: IProduct;
}

const MagicUploadPreview: FC<MagicUploadPreviewProps> = ({ state }) => {
  const classes = useStyles();

  const magicUploadHistory = state?.magicUploadHistory || [];

  if (!magicUploadHistory.length) {
    return null;
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <div className={classes.heading}>
          Magic Upload History ({magicUploadHistory.length} {magicUploadHistory.length === 1 ? 'use' : 'uses'})
        </div>
        <Typography className={classes.secondaryHeading}>View AI-extracted data</Typography>
      </AccordionSummary>
      <div style={{ width: '100%', padding: '0 16px', marginTop: -16 }}>
        {magicUploadHistory.map((entry, index) => {
          const date = new Date(entry.timestamp);
          const formattedDate = date.toLocaleString('sv-SE');
          let model = entry.model || 'AI';
          if (!model.includes('-') && !model.includes('.')) {
            model = model.toUpperCase();
          }

          return (
            <Accordion key={index} className={classes.entryAccordion}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" width="100%" gridGap={16}>
                  <div>
                    #{index + 1}: {model}
                  </div>
                  <Typography className={classes.timestamp}>{formattedDate}</Typography>
                  <Typography className={classes.timestamp}>{entry.imageUrls?.length || 0} image(s)</Typography>
                </Box>
              </AccordionSummary>
              <Box width="100%" px={2}>
                {entry.imageUrls?.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Images used:
                    </Typography>
                    <div className={classes.imageList}>
                      {entry.imageUrls.map((url, imgIndex) => {
                        const name = url.split('/').pop() || '';
                        const extension = name.includes('.') ? name.split('.').pop()?.toLowerCase() : '';
                        const shouldRenderExtensionPreview = ['csv', 'xlsx'].includes(extension || '');

                        if (shouldRenderExtensionPreview) {
                          return (
                            <div
                              key={imgIndex}
                              className={classes.fileThumbail}
                              onClick={() => window.open(url, '_blank')}
                              title={`Open ${name}`}
                            >
                              {extension?.toUpperCase() || 'FILE'}
                            </div>
                          );
                        }

                        return (
                          <img
                            key={imgIndex}
                            src={resize128ImageWithCompression(url)}
                            alt={`Magic upload ${imgIndex + 1}`}
                            className={classes.thumbnail}
                            onClick={() => window.open(url, '_blank')}
                            style={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </div>
                  </Box>
                )}

                <Typography variant="subtitle2" gutterBottom>
                  Raw AI Output:
                </Typography>
                <ReactJson
                  src={entry.rawOutput || {}}
                  enableClipboard={true}
                  iconStyle="triangle"
                  displayDataTypes={false}
                  displayObjectSize={true}
                  indentWidth={2}
                  collapsed={1}
                  name={null}
                  collapseStringsAfterLength={80}
                  quotesOnKeys={false}
                />
              </Box>
            </Accordion>
          );
        })}
      </div>
    </Accordion>
  );
};

export default MagicUploadPreview;
